import { useAppUsers } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useToast } from '~/components/communication/Toast'
import { UsersHeaderActions } from '~/components/routes/users/users-header-actions'
import { USERS_TAB_PERMISSIONS } from '~/components/routes/users/tabs'
import { appUsersColumns, SOLVENCY_COLUMN_FIELDS } from '~/components/routes/users/users-columns'
import { UsersPageLayout, UsersSearchGrid } from '~/components/routes/users/users-page-layout'
import { useDeleteUser } from '~/components/routes/users/use-delete-selection'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/users/app')({
  staticData: {
    permission: USERS_TAB_PERMISSIONS.app,
  },
  component: AppUsersPage,
})
withRoutePermissions(Route)

function AppUsersPage() {
  const [Toast, showToast] = useToast()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number[]>([])

  const canReadSolvency = useMigratedRight(['read', 'users.app-users.solvency'], [])

  const columns = useMemo(
    () =>
      canReadSolvency
        ? appUsersColumns
        : appUsersColumns.filter((column) => !SOLVENCY_COLUMN_FIELDS.includes(column.field)),
    [canReadSolvency],
  )

  const { data: appUsers, refetch, isLoading } = useAppUsers()

  const users = useMemo(() => appUsers ?? [], [appUsers])

  const rows = useMemo(
    () =>
      users.filter((user) =>
        includesLowerCase(`${user.firstName} ${user.lastName} ${user.email}`, search),
      ),
    [users, search],
  )

  const handleDelete = useDeleteUser(selected, showToast, () => {
    setSelected([])
    return refetch()
  })

  return (
    <UsersPageLayout
      activeTab="app"
      actions={
        <UsersHeaderActions activeTab="app" selected={selected} handleDelete={handleDelete} />
      }
    >
      <Toast />
      <UsersSearchGrid
        search={search}
        onSearchChange={setSearch}
        columns={columns}
        rows={rows}
        loading={isLoading}
        onRowSelectionModelChange={setSelected}
      />
    </UsersPageLayout>
  )
}
